import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Table,
  DatePicker,
  Row,
  Col,
  Button,
  Space,
  Typography,
  Statistic,
  Tag,
  message,
} from "antd";
import {
  FileExcelOutlined,
  FilePdfOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import fetchData from "../../functions/fetchData";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const formatCurrency = (value) => Number(value || 0).toFixed(2);

export default function DayCloseSummary() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [dateRange, setDateRange] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await fetchData("day_close_summary", null, "id", {});
      setData(Array.isArray(result) ? result : []);
    } catch (error) {
      message.error("Failed to load day close summary");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredData = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return data;

    const start = dateRange[0].format("YYYY-MM-DD");
    const end = dateRange[1].format("YYYY-MM-DD");

    return data.filter((row) => {
      if (!row.close_date) return false;
      const d = String(row.close_date).slice(0, 10);
      return d >= start && d <= end;
    });
  }, [data, dateRange]);

  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, row) => {
        acc.total_sales += Number(row.total_sales || 0);
        acc.net_sales += Number(row.net_sales || 0);
        acc.total_orders += Number(row.total_orders || 0);
        acc.total_items_sold += Number(row.total_items_sold || 0);
        return acc;
      },
      { total_sales: 0, net_sales: 0, total_orders: 0, total_items_sold: 0 }
    );
  }, [filteredData]);

  const handleExportExcel = () => {
    const exportRows = filteredData.map((row) => ({
      ID: row.id,
      "Close Date": row.close_date,
      "Total Sales": formatCurrency(row.total_sales),
      "Cash Sales": formatCurrency(row.cash_sales),
      "UPI Sales": formatCurrency(row.upi_sales),
      "Card Sales": formatCurrency(row.card_sales),
      "QR Sales": formatCurrency(row.qr_sales),
      "Bank Transfer Sales": formatCurrency(row.bank_transfer_sales),
      "Online Sales": formatCurrency(row.online_sales),
      "Other Sales": formatCurrency(row.other_sales),
      "Total Orders": Number(row.total_orders || 0),
      "Total Items Sold": Number(row.total_items_sold || 0),
      "Discount Amount": formatCurrency(row.discount_amount),
      "Tax Amount": formatCurrency(row.tax_amount),
      "Net Sales": formatCurrency(row.net_sales),
      "Opened By": row.opened_by || "",
      "Closed By": row.closed_by || "",
      "Opening Time": row.opening_time || "",
      "Closing Time": row.closing_time || "",
      Status: row.status || "",
      Notes: row.notes || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Day Close Summary");
    XLSX.writeFile(wb, `Day_Close_Summary_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
    message.success("Excel exported");
  };

  const handleExportPdf = () => {
    const doc = new jsPDF("landscape");
    doc.setFontSize(14);
    doc.text("Day Close Summary", 14, 16);

    if (dateRange?.[0] && dateRange?.[1]) {
      doc.setFontSize(10);
      doc.text(
        `Date Range: ${dateRange[0].format("YYYY-MM-DD")} to ${dateRange[1].format("YYYY-MM-DD")}`,
        14,
        23
      );
    }

    const head = [[
      "ID",
      "Close Date",
      "Total Sales",
      "Cash",
      "UPI",
      "Card",
      "QR",
      "Bank",
      "Online",
      "Other",
      "Orders",
      "Items",
      "Discount",
      "Tax",
      "Net Sales",
      "Closed By",
      "Status",
    ]];

    const body = filteredData.map((row) => [
      row.id,
      row.close_date || "",
      formatCurrency(row.total_sales),
      formatCurrency(row.cash_sales),
      formatCurrency(row.upi_sales),
      formatCurrency(row.card_sales),
      formatCurrency(row.qr_sales),
      formatCurrency(row.bank_transfer_sales),
      formatCurrency(row.online_sales),
      formatCurrency(row.other_sales),
      Number(row.total_orders || 0),
      Number(row.total_items_sold || 0),
      formatCurrency(row.discount_amount),
      formatCurrency(row.tax_amount),
      formatCurrency(row.net_sales),
      row.closed_by || "",
      (row.status || "").toUpperCase(),
    ]);

    doc.autoTable({
      startY: 28,
      head,
      body,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [24, 144, 255] },
    });

    doc.save(`Day_Close_Summary_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`);
    message.success("PDF exported");
  };

  const columns = [
    { title: "ID", dataIndex: "id", key: "id", width: 70, fixed: "left" },
    {
      title: "Close Date",
      dataIndex: "close_date",
      key: "close_date",
      width: 120,
      sorter: (a, b) => String(a.close_date || "").localeCompare(String(b.close_date || "")),
      render: (v) => (v ? String(v).slice(0, 10) : "-"),
    },
    { title: "Total Sales", dataIndex: "total_sales", key: "total_sales", width: 120, align: "right", render: (v) => `฿${formatCurrency(v)}` },
    { title: "Cash", dataIndex: "cash_sales", key: "cash_sales", width: 100, align: "right", render: (v) => `฿${formatCurrency(v)}` },
    { title: "UPI", dataIndex: "upi_sales", key: "upi_sales", width: 100, align: "right", render: (v) => `฿${formatCurrency(v)}` },
    { title: "Card", dataIndex: "card_sales", key: "card_sales", width: 100, align: "right", render: (v) => `฿${formatCurrency(v)}` },
    { title: "QR", dataIndex: "qr_sales", key: "qr_sales", width: 100, align: "right", render: (v) => `฿${formatCurrency(v)}` },
    { title: "Bank Transfer", dataIndex: "bank_transfer_sales", key: "bank_transfer_sales", width: 130, align: "right", render: (v) => `฿${formatCurrency(v)}` },
    { title: "Online", dataIndex: "online_sales", key: "online_sales", width: 110, align: "right", render: (v) => `฿${formatCurrency(v)}` },
    { title: "Other", dataIndex: "other_sales", key: "other_sales", width: 100, align: "right", render: (v) => `฿${formatCurrency(v)}` },
    { title: "Orders", dataIndex: "total_orders", key: "total_orders", width: 90, align: "right" },
    { title: "Items Sold", dataIndex: "total_items_sold", key: "total_items_sold", width: 100, align: "right" },
    { title: "Discount", dataIndex: "discount_amount", key: "discount_amount", width: 100, align: "right", render: (v) => `฿${formatCurrency(v)}` },
    { title: "Tax", dataIndex: "tax_amount", key: "tax_amount", width: 100, align: "right", render: (v) => `฿${formatCurrency(v)}` },
    { title: "Net Sales", dataIndex: "net_sales", key: "net_sales", width: 120, align: "right", render: (v) => `฿${formatCurrency(v)}` },
    { title: "Opened By", dataIndex: "opened_by", key: "opened_by", width: 100, render: (v) => v || "-" },
    { title: "Closed By", dataIndex: "closed_by", key: "closed_by", width: 100, render: (v) => v || "-" },
    { title: "Opening Time", dataIndex: "opening_time", key: "opening_time", width: 170, render: (v) => v || "-" },
    { title: "Closing Time", dataIndex: "closing_time", key: "closing_time", width: 170, render: (v) => v || "-" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 95,
      render: (v) => (
        <Tag color={String(v).toLowerCase() === "closed" ? "green" : "orange"}>
          {String(v || "-").toUpperCase()}
        </Tag>
      ),
    },
    { title: "Notes", dataIndex: "notes", key: "notes", width: 180, render: (v) => v || "-" },
  ];

  return (
    <Layout>
      <Header title="Day Close Summary" />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small"><Statistic title="Records" value={filteredData.length} /></Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small"><Statistic title="Total Sales" value={totals.total_sales} precision={2} prefix="฿" /></Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small"><Statistic title="Net Sales" value={totals.net_sales} precision={2} prefix="฿" /></Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small"><Statistic title="Orders / Items" value={`${totals.total_orders} / ${totals.total_items_sold}`} /></Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={10} lg={8}>
            <Text style={{ display: "block", marginBottom: 6 }}>Date Range</Text>
            <RangePicker
              style={{ width: "100%" }}
              value={dateRange}
              onChange={(val) => setDateRange(val)}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>
          <Col xs={24} md={14} lg={16}>
            <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>Refresh</Button>
              <Button icon={<FilePdfOutlined />} danger onClick={handleExportPdf}>Export PDF</Button>
              <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>Export Excel</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card size="small">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredData}
          columns={columns}
          scroll={{ x: 2300 }}
          size="small"
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ["15", "30", "50", "100"],
            showLessItems: true,
            showTotal: (total) => `Total ${total} records`,
          }}
        />
      </Card>
    </Layout>
  );
}
